// Based on LmInput from @tamagui-extras/form
// https://github.com/dohomi/tamagui-kitchen-sink/blob/master/packages/form/src/LmInput.tsx

import { Eye, EyeOff } from "@tamagui/lucide-icons";
import { forwardRef, useState } from "react";
import {
  GetRef,
  Input as TamaguiInput,
  InputProps as TamaguiInputProps,
  Stack,
  styled,
  SizableText,
  View,
  ColorTokens,
  ButtonProps,
  GetProps,
} from "tamagui";
import { getThemedIcon, tokens } from "tamagui-config";

import {
  FormFieldContainer,
  FormFieldContainerBaseProps,
} from "./FormFieldContainer";

export interface ExtraInputProps {
  /**
   * If `true`, the input field will mask the entered text, typically used for password inputs.
   */
  isPassword?: boolean;
  /**
   * Forces the input field to appear hovered.
   */
  hovered?: boolean;
  /**
   * Forces the input field to appear focused.
   */
  focused?: boolean;
  /**
   * The prefix to display before the input field.
   */
  prefix?: string;
  /**
   * The suffix to display after the input field.
   */
  suffix?: string;
  /**
   * The icon to display before the input field.
   */
  leftIcon?: ButtonProps["icon"];
  /**
   * The icon to display after the input field.
   */
  rightIcon?: ButtonProps["icon"];
}

export interface InputProps
  extends TamaguiInputProps,
    FormFieldContainerBaseProps,
    ExtraInputProps {}

export const Input = forwardRef<GetRef<typeof TamaguiInput>, InputProps>(
  function Input(
    {
      required,
      error,
      hovered,
      focused,
      label,
      labelProps,
      helperText,
      helperTextProps,
      containerProps,
      prefix,
      suffix,
      leftIcon,
      rightIcon,
      ...rest
    },
    ref,
  ) {
    const isPassword = rest.isPassword || rest.secureTextEntry;
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isSensitiveText = isPassword && !isPasswordVisible;

    const rightIconFinal = isPassword
      ? isPasswordVisible
        ? EyeOff
        : Eye
      : rightIcon;
    const rightIconOnPress = isPassword
      ? () => setIsPasswordVisible((state) => !state)
      : undefined;

    const inputContainerProps = {
      required,
      error,
      label,
      labelProps,
      helperText,
      helperTextProps,
      ...containerProps,
    };

    const inputProps = {
      error,
      ...rest,
    };

    const isSelectInput = rest.editable === false;
    const showFakePlaceholder =
      isSelectInput && !rest.value && rest.placeholder;

    return (
      <FormFieldContainer {...inputContainerProps}>
        <Stack position="relative">
          <LeftAccessory
            text={prefix}
            icon={leftIcon}
            inputProps={inputProps}
          />
          {showFakePlaceholder ? (
            <FakeInputPlaceholder
              placeholder={rest.placeholder}
              isRequired={required}
              error={error}
            />
          ) : (
            <StyledInput
              ref={ref}
              secureTextEntry={isSensitiveText}
              forceHoverStyle={hovered}
              forceFocusStyle={focused}
              withLeftText={!!prefix}
              withLeftIcon={!!leftIcon}
              withRightText={!!suffix}
              withRightIcon={!!rightIcon}
              {...inputProps}
            />
          )}
          <RightAccessory
            text={suffix}
            icon={rightIconFinal}
            onPress={rightIconOnPress}
            inputProps={inputProps}
          />
        </Stack>
      </FormFieldContainer>
    );
  },
);

const hoverStyle: GetProps<typeof TamaguiInput> = {
  borderColor: "$white11",
};

const focusStyle: GetProps<typeof TamaguiInput> = {
  borderColor: "$white7",
};

const StyledInput = styled(TamaguiInput, {
  paddingHorizontal: "$3.5",
  placeholderTextColor: "$gray9",
  backgroundColor: "$white5",
  borderColor: "$white10",
  hoverStyle: {
    ...hoverStyle,
  },
  focusStyle: {
    ...focusStyle,
  },
  disabledStyle: {
    backgroundColor: "$color2",
    borderColor: "$color8",
    placeholderTextColor: "$color9",
  },
  variants: {
    unstyled: {
      false: (_, { props }) => ({
        // @ts-expect-error
        ...(props.withLeftText && { paddingLeft: "$7" }),
        // @ts-expect-error
        ...(props.withLeftIcon && { paddingLeft: "$8" }),
        // @ts-expect-error
        ...(props.withRightText && { paddingRight: "$7" }),
        // @ts-expect-error
        ...(props.withRightIcon && { paddingRight: "$8" }),
      }),
    },
    multiline: {
      true: {
        height: "auto",
        minHeight: "$4",
      },
    },
    error: {
      true: {
        backgroundColor: "$color2",
        borderColor: "$color10",
      },
    },
    forceHoverStyle: {
      true: {
        ...hoverStyle,
      },
    },
    forceFocusStyle: {
      true: {
        ...focusStyle,
      },
    },
    withLeftText: {
      true: {}, // Leave this so TypeScript knows about the prop
    },
    withLeftIcon: {
      true: {}, // Leave this so TypeScript knows about the prop
    },
    withRightText: {
      true: {}, // Leave this so TypeScript knows about the prop
    },
    withRightIcon: {
      true: {}, // Leave this so TypeScript knows about the prop
    },
  } as const,
});

type InputAccessoryProps = {
  after?: boolean;
  text?: string;
  icon?: ButtonProps["icon"];
  onPress?: () => void;
  inputProps: InputProps;
};

const InputAccessory = ({
  after,
  text,
  icon,
  onPress,
  inputProps: { color: textColor, placeholderTextColor },
}: InputAccessoryProps) => {
  const color = (placeholderTextColor ??
    textColor ??
    "$color10") as ColorTokens;

  const Container = after ? RightAccessoryContainer : LeftAccessoryContainer;
  const containerProps = { onPress };
  const textProps = { color };

  const iconElement = icon && getThemedIcon(icon, { size: "$1.5", color });

  return (
    <Container {...containerProps}>
      {text ? <SizableText {...textProps}>{text}</SizableText> : iconElement}
    </Container>
  );
};

const LeftAccessory = (props: Omit<InputAccessoryProps, "after">) => (
  <InputAccessory {...props} />
);

const RightAccessory = (props: Omit<InputAccessoryProps, "after">) => (
  <InputAccessory after {...props} />
);

const AccessoryContainer = styled(View, {
  height: "$4",
  position: "absolute",
  zIndex: "$1",
  justifyContent: "center",
});

const LeftAccessoryContainer = styled(AccessoryContainer, {
  left: "$3.5",
});

const RightAccessoryContainer = styled(AccessoryContainer, {
  right: "$3.5",
});

const FakeInputPlaceholder = ({
  placeholder,
  isRequired,
  error,
}: {
  placeholder?: string;
  isRequired?: boolean;
  error?: boolean;
}) => (
  <Stack
    height="$4"
    justifyContent="center"
    borderColor={error ? "$color10" : "$color6"}
    backgroundColor="$backgroundOpaque"
    paddingHorizontal="$3.5"
    borderWidth={1}
    borderRadius="$3"
  >
    <SizableText color="$white10">
      {placeholder}
      {isRequired && (
        <SizableText color={tokens.color.warningRed}> *</SizableText>
      )}
    </SizableText>
  </Stack>
);
